import { useCallback, useEffect, useRef, useState } from 'react';
import type { ArticleDto, ArticlesOverviewDto, ArticlesSyncStatusDto } from '@ciimar/shared';
import { PortalModule } from '@ciimar/shared';
import { api } from '../api/client';
import { useAccess } from '../auth/AccessContext';
import { LIST_PAGE_SIZE, usePersistedState, useScrollRestore, scrollListToTop } from '../lib/list';
import { Combobox } from '../components/Combobox';
import { useI18n, useT } from '../lib/i18n';

const MONTH_NAMES: Record<'pt' | 'en', string[]> = {
  pt: [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
};

interface Filters {
  q: string;
  year: number | '';
  /** Publication month 1–12, or '' for all. */
  month: number | '';
  type: string;
  openAccess: boolean;
  /** Only articles with at least one citation. */
  cited: boolean;
  /** Person.id */
  person: string;
  /** Team.code */
  team: string;
  /** Q1–Q4 */
  quartile: string;
}

const EMPTY_FILTERS: Filters = {
  q: '',
  year: '',
  month: '',
  type: '',
  openAccess: false,
  cited: false,
  person: '',
  team: '',
  quartile: '',
};

function fmtDateTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function Articles() {
  const t = useT();
  const { lang } = useI18n();
  const { canEdit } = useAccess();
  const editable = canEdit(PortalModule.Artigos);

  const [filters, setFilters] = usePersistedState<Filters>('articles:filters', EMPTY_FILTERS);
  // The search box is debounced into `filters.q`, so typing doesn't refetch per key.
  const [searchText, setSearchText] = useState(filters.q);

  const [items, setItems] = useState<ArticleDto[]>([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState<Omit<ArticlesOverviewDto, 'articles' | 'total'> | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<ArticlesSyncStatusDto | null>(null);
  const pollRef = useRef<number | null>(null);

  // Keep the latest list length available to the loader without re-binding it.
  const lenRef = useRef(0);
  lenRef.current = items.length;
  const totalRef = useRef(0);
  totalRef.current = total;
  const loadingRef = useRef(false);

  const load = useCallback(
    async (offset: number) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      if (offset === 0) setLoading(true);
      else setLoadingMore(true);
      try {
        const res = await api.articlesOverview({
          q: filters.q || undefined,
          year: filters.year || undefined,
          month: filters.month || undefined,
          type: filters.type || undefined,
          openAccess: filters.openAccess || undefined,
          cited: filters.cited || undefined,
          person: filters.person || undefined,
          team: filters.team || undefined,
          quartile: filters.quartile || undefined,
          limit: LIST_PAGE_SIZE,
          offset,
        });
        setTotal(res.total);
        setMeta(res);
        setError(res.error ?? null);
        setItems((prev) => (offset === 0 ? res.articles : [...prev, ...res.articles]));
      } catch (e) {
        setError((e as Error).message);
      } finally {
        loadingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [
      filters.q,
      filters.year,
      filters.month,
      filters.type,
      filters.openAccess,
      filters.cited,
      filters.person,
      filters.team,
      filters.quartile,
    ],
  );

  // Reload from the top whenever a filter changes.
  useEffect(() => {
    scrollListToTop();
    void load(0);
  }, [load]);

  // Debounce the search box into the persisted filter.
  useEffect(() => {
    if (searchText === filters.q) return;
    const t = setTimeout(() => setFilters({ ...filters, q: searchText }), 300);
    return () => clearTimeout(t);
  }, [searchText, filters, setFilters]);

  // Infinite scroll: load the next page when the sentinel nears the viewport.
  const sentinelRef = useCallback(
    (el: HTMLElement | null) => {
      if (!el) return;
      const io = new IntersectionObserver(
        (entries) => {
          if (
            entries.some((e) => e.isIntersecting) &&
            !loadingRef.current &&
            lenRef.current < totalRef.current
          ) {
            void load(lenRef.current);
          }
        },
        { rootMargin: '600px 0px' },
      );
      io.observe(el);
      return () => io.disconnect();
    },
    [load],
  );

  useScrollRestore('articles:scroll', !loading && items.length > 0);

  // Keep the loader reachable from the poll loop without re-creating it.
  const loadRef = useRef(load);
  loadRef.current = load;

  // Poll the background sync's progress; when it ends, refresh the list.
  const poll = useCallback(async () => {
    try {
      const st = await api.articlesSyncStatus();
      setSyncStatus(st);
      if (st.running) {
        pollRef.current = window.setTimeout(() => void poll(), 2000);
      } else {
        await loadRef.current(0);
      }
    } catch {
      /* stop polling silently on a transient error */
    }
  }, []);

  // On mount, resume following a sync that may already be running.
  useEffect(() => {
    let alive = true;
    void api
      .articlesSyncStatus()
      .then((st) => {
        if (!alive) return;
        setSyncStatus(st);
        if (st.running) void poll();
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [poll]);

  async function sync() {
    try {
      const st = await api.startArticlesSync();
      setSyncStatus(st);
      if (st.running) void poll();
      else await load(0);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const running = syncStatus?.running ?? false;
  let syncLabel: string;
  if (running) {
    const done = syncStatus?.fetched ?? 0;
    syncLabel = syncStatus?.total
      ? t('articles.sync.progressTotal', {
          done,
          total: Math.min(syncStatus.total, 5000).toLocaleString('pt-PT'),
        })
      : t('articles.sync.progress', { done });
  } else {
    syncLabel = t('articles.sync.button');
  }

  const facets = meta?.facets ?? {
    years: [],
    months: [],
    types: [],
    teams: [],
    people: [],
    quartiles: [],
  };
  const hasFilters =
    filters.q !== '' ||
    filters.year !== '' ||
    filters.month !== '' ||
    filters.type !== '' ||
    filters.openAccess ||
    filters.cited ||
    filters.person !== '' ||
    filters.team !== '' ||
    filters.quartile !== '';

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('articles.title')}</h2>
          <p className="page-sub">
            {t('articles.sub.main')}
            {meta?.totalReported != null &&
              ` · ${t('articles.sub.scopusTotal', { n: meta.totalReported.toLocaleString('pt-PT') })}`}
            {meta?.syncedAt &&
              ` · ${t('articles.sub.synced', { date: fmtDateTime(meta.syncedAt) })}`}
          </p>
        </div>
        <div className="spacer" style={{ flex: 1 }} />
        {meta && (
          <span className={`badge ${meta.source === 'rest' ? 'approved' : 'changes'}`}>
            {t(`articles.source.${meta.source}`)}
          </span>
        )}
        {editable && (
          <button
            type="button"
            className="secondary"
            onClick={() => void sync()}
            disabled={running}
          >
            {syncLabel}
          </button>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}
      {syncStatus?.error && !running && (
        <div className="alert error">{t('articles.sync.failed', { error: syncStatus.error })}</div>
      )}
      {meta?.source === 'stub' && (
        <div className="alert">
          {t('articles.stub.intro')}
          <code> ELSEVIER_API_KEY</code> {t('articles.stub.and')} <code>SCOPUS_MODE=rest</code>{' '}
          {t('articles.stub.outro')}
        </div>
      )}

      <div className="erp-filters" style={{ marginBottom: 16 }}>
        <label className="erp-search">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          >
            <path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14M21 21l-5-5" />
          </svg>
          <input
            placeholder={t('articles.filters.searchPlaceholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </label>
        <div className="article-combo">
          <Combobox
            value={filters.team}
            options={facets.teams.map((tm) => ({ value: tm.code, label: tm.name }))}
            onChange={(team) => setFilters({ ...filters, team })}
            placeholder={t('articles.filters.allTeams')}
          />
        </div>
        <div className="article-combo">
          <Combobox
            value={filters.person}
            options={facets.people.map((p) => ({
              value: p.personId,
              label: p.code ? `${p.name} · ${p.code}` : p.name,
            }))}
            onChange={(person) => setFilters({ ...filters, person })}
            placeholder={t('articles.filters.allResearchers')}
          />
        </div>
        <select
          className="erp-select"
          value={filters.year === '' ? '' : String(filters.year)}
          onChange={(e) =>
            setFilters({ ...filters, year: e.target.value ? Number(e.target.value) : '' })
          }
        >
          <option value="">{t('articles.filters.allYears')}</option>
          {facets.years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        {facets.months.length > 0 && (
          <select
            className="erp-select"
            value={filters.month === '' ? '' : String(filters.month)}
            onChange={(e) =>
              setFilters({ ...filters, month: e.target.value ? Number(e.target.value) : '' })
            }
          >
            <option value="">{t('articles.filters.allMonths')}</option>
            {facets.months.map((m) => (
              <option key={m} value={m}>
                {MONTH_NAMES[lang][m - 1] ?? m}
              </option>
            ))}
          </select>
        )}
        <select
          className="erp-select"
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
        >
          <option value="">{t('articles.filters.allTypes')}</option>
          {facets.types.map((ty) => (
            <option key={ty} value={ty}>
              {ty}
            </option>
          ))}
        </select>
        {facets.quartiles.length > 0 && (
          <select
            className="erp-select"
            value={filters.quartile}
            onChange={(e) => setFilters({ ...filters, quartile: e.target.value })}
          >
            <option value="">{t('articles.filters.allQuartiles')}</option>
            {facets.quartiles.map((qt) => (
              <option key={qt} value={qt}>
                {qt}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          className={`filter-chip ${filters.openAccess ? 'on' : ''}`}
          onClick={() => setFilters({ ...filters, openAccess: !filters.openAccess })}
        >
          {t('articles.filters.openAccess')}
        </button>
        <button
          type="button"
          className={`filter-chip ${filters.cited ? 'on' : ''}`}
          onClick={() => setFilters({ ...filters, cited: !filters.cited })}
        >
          {t('articles.filters.cited')}
        </button>
        {hasFilters && (
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              setSearchText('');
              setFilters(EMPTY_FILTERS);
            }}
          >
            {t('articles.filters.clear')}
          </button>
        )}
      </div>

      {loading ? (
        <p className="muted">{t('articles.loading')}</p>
      ) : items.length === 0 ? (
        <div className="erp-panel">
          <div className="panel-empty is-empty">
            {hasFilters ? t('articles.empty.filtered') : t('articles.empty.none')}
          </div>
        </div>
      ) : (
        <>
          <p className="page-sub" style={{ marginBottom: 10 }}>
            {total === 1
              ? t('articles.count.one', { n: total.toLocaleString('pt-PT') })
              : t('articles.count.many', { n: total.toLocaleString('pt-PT') })}
          </p>
          <div className="article-list">
            {items.map((a) => (
              <ArticleCard key={a.scopusId} article={a} />
            ))}
          </div>
          {items.length < total && (
            <div ref={sentinelRef} className="list-sentinel">
              {loadingMore ? t('articles.loadingMore') : ''}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ArticleCard({ article: a }: { article: ArticleDto }) {
  const t = useT();
  const meta = [
    a.publicationName,
    a.year ? String(a.year) : null,
    a.volume ? t('articles.card.volume', { n: a.volume }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <article className="erp-panel article-card">
      <div className="article-main">
        <h3 className="article-title">
          {a.scopusUrl ? (
            <a href={a.scopusUrl} target="_blank" rel="noopener noreferrer">
              {a.title}
            </a>
          ) : (
            a.title
          )}
        </h3>
        {meta && <div className="article-meta">{meta}</div>}
        {a.creator && <div className="article-authors">{a.creator}</div>}
        {a.authors.length > 0 && (
          <div className="article-ciimar" title={t('articles.card.ciimarAuthors')}>
            {a.authors.map((au) => (
              <span key={au.personId} className="article-person">
                {au.name}
              </span>
            ))}
          </div>
        )}
        <div className="article-tags">
          {a.quartile && (
            <span
              className={`article-tag quartile ${a.quartile.toLowerCase()}`}
              title={
                a.citeScorePercentile != null
                  ? t('articles.card.quartileTipPercentile', {
                      q: a.quartile,
                      p: a.citeScorePercentile,
                    })
                  : t('articles.card.quartileTip', { q: a.quartile })
              }
            >
              {a.quartile}
            </span>
          )}
          {a.typeDescription && <span className="article-tag">{a.typeDescription}</span>}
          {a.openAccess && <span className="article-tag oa">{t('articles.card.openAccess')}</span>}
          {a.doi && (
            <a
              className="article-tag link"
              href={`https://doi.org/${a.doi}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              DOI
            </a>
          )}
        </div>
      </div>
      <div className="article-cites" title={t('articles.card.citationsTip')}>
        <strong>{a.citedByCount ?? 0}</strong>
        <span>
          {a.citedByCount === 1 ? t('articles.card.citationOne') : t('articles.card.citationMany')}
        </span>
      </div>
    </article>
  );
}
